import { Suspense, lazy, useEffect, useRef, useState } from "react";
const ReactPlayer = lazy(() => import("react-player"));
import { useRetry } from "../../hooks/useRetry";
import VideoErrorBox from "./VideoErrorBox";
import VideoRetryBox from "./VideoRetryBox";
import VideoBlockedBox from "./VideoBlockedBox";
import LoadingScreen from "../../components/LoadingScreen";
import clsx from "clsx";

// If onReady doesn't fire within this window we assume the Vimeo iframe
// is silently blocked (ad blocker, privacy extension, CDN filtering, etc.)
// since ReactPlayer's onError does not catch iframe-level network failures.
const WATCHDOG_MS = 20000;

export default function VideoMain({
  url = "https://player.vimeo.com/video/829916057",
  setTime,
  onEnd,
  isLive = false,
  fitParent = false,
}: {
  url?: string;
  setTime: (s: Date) => void;
  onEnd?: () => void;
  isLive?: boolean;
  fitParent?: boolean;
}) {
  const { retryStatus, retry, reset } = useRetry();
  const [isBlocked, setIsBlocked] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);
  const watchdogRef = useRef<number | null>(null);

  useEffect(() => {
    setIsBlocked(false);
    if (watchdogRef.current) clearTimeout(watchdogRef.current);
    watchdogRef.current = window.setTimeout(() => {
      setIsBlocked(true);
    }, WATCHDOG_MS);

    return () => {
      if (watchdogRef.current) {
        clearTimeout(watchdogRef.current);
        watchdogRef.current = null;
      }
    };
  }, [url, reloadNonce]);

  const handleDuration = (s: number) => {
    const expiry = new Date(Date.now() + (s + 20) * 1000);
    setTime(expiry);
  };

  const handleReady = () => {
    if (watchdogRef.current) {
      clearTimeout(watchdogRef.current);
      watchdogRef.current = null;
    }
    if (retryStatus.retryCount > 0) reset();
  };

  const handleBlockedRetry = () => {
    setReloadNonce((n) => n + 1);
  };

  return (
    <div className="flex-1 p-2 md:p-3">
      <div className={clsx(
        "relative w-full h-full overflow-hidden rounded-xl",
        fitParent ? "" : "max-h-[90vh]"
      )}>
        {isBlocked ? (
          <VideoBlockedBox reset={handleBlockedRetry} />
        ) : retryStatus.doesPersist ? (
          <VideoErrorBox reset={reset} />
        ) : (
          <>
            {retryStatus.retryCount > 0 && (
              <VideoRetryBox count={retryStatus.retryCount} />
            )}

            <Suspense fallback={<LoadingScreen text="Video yükleniyor..." />}>
              <ReactPlayer
                key={url + retryStatus.retryCount + reloadNonce}
                className="!absolute !inset-0"
                controls
                playing={isLive}
                onDuration={handleDuration}
                onError={(e: { message: string; stack: never }) =>
                  retry(retryStatus.retryCount, e)
                }
                config={{
                  youtube: {
                    playerVars: {
                      showinfo: 1,
                      iv_load_policy: 3,
                      autoplay: isLive ? 1 : 0,
                    },
                  },
                  vimeo: {
                    playerOptions: {
                      autoplay: isLive ? 1 : 0,
                      color: "71D000",
                      title: false,
                      byline: false,
                      portrait: false,
                      dnt: true,
                      quality: "auto",
                      vimeo_logo: false,
                      badge: false,
                    },
                  },
                }}
                onEnded={onEnd}
                url={url}
                onReady={handleReady}
                width={retryStatus.retryCount ? 0 : "100%"}
                height={retryStatus.retryCount ? 0 : "100%"}
              />
            </Suspense>
          </>
        )}
      </div>
    </div>
  );
}
